import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getExam, saveExamSection } from '../../services/examService';
import EXAM_SECTIONS, { getExamSectionByNumber } from '../../constants/examConfig';

const ExamForm = () => {
  const { patientId, caseId, sectionId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const tabsRef = useRef(null);

  const sectionNum = parseInt(sectionId);
  const section = getExamSectionByNumber(sectionNum);

  const [examData, setExamData] = useState({});
  const [sectionData, setSectionData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadExam();
  }, [patientId, caseId, userProfile]);

  useEffect(() => {
    if (examData && section) {
      setSectionData(examData[section.id] || {});
    }
  }, [sectionNum, examData, section]);

  useEffect(() => {
    // Scroll active tab into view
    if (tabsRef.current) {
      const activeTab = tabsRef.current.querySelector('[data-active="true"]');
      if (activeTab) {
        activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [sectionNum]);

  const loadExam = async () => {
    if (!userProfile) return;
    try {
      setLoading(true);
      const exam = await getExam(userProfile.practiceId, patientId, caseId);
      setExamData(exam || {});
    } catch (err) {
      console.error('Error loading exam:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (fieldId, value) => {
    setSectionData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSave = async () => {
    if (!userProfile || !section) return;
    try {
      await saveExamSection(userProfile.practiceId, patientId, caseId, section.id, sectionData);
      setExamData(prev => ({ ...prev, [section.id]: sectionData }));
    } catch (err) {
      console.error('Error saving:', err);
    }
  };

  const goToSection = async (num) => {
    await handleSave();
    navigate(`/patients/${patientId}/cases/${caseId}/exam/${num}`, { replace: true });
  };

  const handleNext = async () => {
    await handleSave();
    if (sectionNum < 17) {
      navigate(`/patients/${patientId}/cases/${caseId}/exam/${sectionNum + 1}`, { replace: true });
    } else {
      navigate(`/patients/${patientId}/cases/${caseId}/exam`);
    }
  };

  const handlePrev = async () => {
    await handleSave();
    if (sectionNum > 1) {
      navigate(`/patients/${patientId}/cases/${caseId}/exam/${sectionNum - 1}`, { replace: true });
    }
  };

  // ─── Field Renderers ─────────────────────────────────────

  const renderTextarea = (field) => (
    <div key={field.id} className={field.linkedTo ? '' : 'mb-5'}>
      {field.label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
      )}
      <textarea
        rows={4}
        placeholder="Type here..."
        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-gray-50"
        value={sectionData[field.id] || ''}
        onChange={(e) => updateField(field.id, e.target.value)}
      />
    </div>
  );

  const renderRadio = (field) => (
    <div key={field.id} className="mb-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <label className="text-sm font-medium text-gray-700 min-w-0 sm:min-w-[200px]">{field.label}</label>
        <div className="flex items-center gap-3 flex-wrap">
          {field.options.map(opt => {
            const isSelected = sectionData[field.id] === opt;
            const colorMap = {
              LOW: 'border-gray-300 text-gray-600',
              MEDIUM: 'border-yellow-400 text-yellow-700',
              HIGH: 'border-red-400 text-red-700',
              NONE: 'border-gray-300 text-gray-600',
              NORMAL: 'border-green-400 text-green-700',
            };
            return (
              <label key={opt} className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name={`${section.id}-${field.id}`}
                  checked={isSelected}
                  onChange={() => updateField(field.id, opt)}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className={`text-sm ${isSelected ? 'font-semibold' : 'text-gray-500'}`}>{opt}</span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderCheckbox = (field) => (
    <div key={field.id} className="mb-4">
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={sectionData[field.id] || false}
          onChange={(e) => updateField(field.id, e.target.checked)}
          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
        />
        <span className="text-sm font-medium text-gray-700">{field.label}</span>
      </label>
    </div>
  );

  const renderNumber = (field) => (
    <div key={field.id} className="mb-5">
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-gray-700 min-w-[200px]">{field.label}</label>
        <input
          type="number"
          className="w-24 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={sectionData[field.id] || ''}
          onChange={(e) => updateField(field.id, e.target.value)}
        />
      </div>
    </div>
  );

  const renderSelect = (field) => (
    <div key={field.id} className="mb-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <label className="text-sm font-medium text-gray-700 min-w-0 sm:min-w-[200px]">{field.label}</label>
        <select
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={sectionData[field.id] || ''}
          onChange={(e) => updateField(field.id, e.target.value)}
        >
          <option value="">Select...</option>
          {field.options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderWnlTable = () => {
    const rows = sectionData.rows || {};
    const updateRow = (name, key, val) => {
      const updated = { ...rows, [name]: { ...(rows[name] || {}), [key]: val } };
      updateField('rows', updated);
    };

    return (
      <div className="overflow-x-auto mb-5">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-blue-400 border-b border-gray-100">
              <th className="py-2 pr-4 font-medium">Name</th>
              <th className="py-2 px-4 font-medium text-center">WNL</th>
              <th className="py-2 px-4 font-medium text-center">Pathology</th>
              <th className="py-2 pl-4 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {section.rows.map((name, i) => {
              const row = rows[name] || {};
              return (
                <tr key={name} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="py-3 pr-4 font-medium text-gray-800">{name}</td>
                  <td className="py-3 px-4 text-center">
                    <input
                      type="radio"
                      name={`wnl-${name}`}
                      checked={row.status === 'wnl'}
                      onChange={() => updateRow(name, 'status', 'wnl')}
                      className="w-5 h-5 text-green-500 border-gray-300 focus:ring-green-500"
                      style={{ accentColor: '#34d399' }}
                    />
                  </td>
                  <td className="py-3 px-4 text-center">
                    <input
                      type="radio"
                      name={`wnl-${name}`}
                      checked={row.status === 'pathology'}
                      onChange={() => updateRow(name, 'status', 'pathology')}
                      className="w-5 h-5 text-red-500 border-gray-300 focus:ring-red-500"
                      style={{ accentColor: '#f87171' }}
                    />
                  </td>
                  <td className="py-3 pl-4">
                    <input
                      type="text"
                      placeholder="Type here..."
                      className="w-full bg-transparent text-sm text-gray-700 focus:outline-none placeholder-gray-400"
                      value={row.description || ''}
                      onChange={(e) => updateRow(name, 'description', e.target.value)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderYesNoTable = () => {
    const answers = sectionData.answers || {};
    const updateAnswer = (idx, key, val) => {
      const updated = { ...answers, [idx]: { ...(answers[idx] || {}), [key]: val } };
      updateField('answers', updated);
    };

    return (
      <div className="overflow-x-auto mb-5">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-blue-400 border-b border-gray-100">
              <th className="py-2 pr-4 font-medium" style={{ minWidth: 280 }}>Question</th>
              <th className="py-2 px-2 font-medium text-center" style={{ width: 80 }}>Answer</th>
              <th className="py-2 pl-4 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {section.questions.map((q, i) => {
              const ans = answers[i] || {};
              return (
                <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="py-3 pr-4 text-gray-800 leading-snug">{q}</td>
                  <td className="py-3 px-2">
                    <div className="flex items-center justify-center gap-2">
                      <label className="inline-flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name={`yn-${i}`}
                          checked={ans.answer === 'yes'}
                          onChange={() => updateAnswer(i, 'answer', 'yes')}
                          className="w-4 h-4"
                          style={{ accentColor: '#f87171' }}
                        />
                        <span className={`text-xs ${ans.answer === 'yes' ? 'font-semibold text-red-600' : 'text-gray-500'}`}>Yes</span>
                      </label>
                      <label className="inline-flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name={`yn-${i}`}
                          checked={ans.answer === 'no'}
                          onChange={() => updateAnswer(i, 'answer', 'no')}
                          className="w-4 h-4"
                          style={{ accentColor: '#34d399' }}
                        />
                        <span className={`text-xs ${ans.answer === 'no' ? 'font-semibold text-green-700' : 'text-gray-500'}`}>No</span>
                      </label>
                    </div>
                  </td>
                  <td className="py-3 pl-4">
                    <input
                      type="text"
                      placeholder="Type here..."
                      className="w-full bg-transparent text-sm text-gray-700 focus:outline-none placeholder-gray-400"
                      value={ans.description || ''}
                      onChange={(e) => updateAnswer(i, 'description', e.target.value)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderMusclePalpation = () => {
    const data = sectionData.muscleData || {};
    const updateMuscle = (group, muscle, key, val) => {
      const gKey = group.replace(/\s/g, '_');
      const updated = {
        ...data,
        [gKey]: {
          ...(data[gKey] || {}),
          [muscle]: { ...(data[gKey]?.[muscle] || {}), [key]: val }
        }
      };
      updateField('muscleData', updated);
      // Update summary flags
      if (group === 'Right Side') updateField('rightSide', true);
      if (group === 'Left Side') updateField('leftSide', true);
    };

    return (
      <div className="space-y-6 mb-5">
        {section.groups.map((group) => {
          const gKey = group.name.replace(/\s/g, '_');
          const groupData = data[gKey] || {};
          return (
            <div key={group.name}>
              <h3 className="text-sm font-bold text-gray-800 mb-2">{group.name}</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-blue-400 border-b border-gray-100">
                      <th className="py-2 pr-4 font-medium">Muscle</th>
                      <th className="py-2 px-4 font-medium text-center">WNL</th>
                      <th className="py-2 px-4 font-medium text-center">Pathology</th>
                      <th className="py-2 pl-4 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.muscles.map((muscle, i) => {
                      const m = groupData[muscle] || {};
                      return (
                        <tr key={muscle} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="py-2.5 pr-4 font-medium text-gray-800">{muscle}</td>
                          <td className="py-2.5 px-4 text-center">
                            <input
                              type="radio"
                              name={`muscle-${gKey}-${muscle}`}
                              checked={m.status === 'wnl'}
                              onChange={() => updateMuscle(group.name, muscle, 'status', 'wnl')}
                              className="w-4 h-4"
                              style={{ accentColor: '#34d399' }}
                            />
                          </td>
                          <td className="py-2.5 px-4 text-center">
                            <input
                              type="radio"
                              name={`muscle-${gKey}-${muscle}`}
                              checked={m.status === 'pathology'}
                              onChange={() => updateMuscle(group.name, muscle, 'status', 'pathology')}
                              className="w-4 h-4"
                              style={{ accentColor: '#f87171' }}
                            />
                          </td>
                          <td className="py-2.5 pl-4">
                            <input
                              type="text"
                              placeholder="Type here..."
                              className="w-full bg-transparent text-sm focus:outline-none placeholder-gray-400"
                              value={m.description || ''}
                              onChange={(e) => updateMuscle(group.name, muscle, 'description', e.target.value)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'textarea': return renderTextarea(field);
      case 'radio': return renderRadio(field);
      case 'checkbox': return renderCheckbox(field);
      case 'number': return renderNumber(field);
      case 'select': return renderSelect(field);
      default: return null;
    }
  };

  const renderSectionContent = () => {
    if (!section) return <p>Section not found</p>;

    switch (section.type) {
      case 'wnlTable':
        return (
          <>
            {renderWnlTable()}
            {section.fields.map(renderField)}
          </>
        );
      case 'yesNoTable':
        return (
          <>
            {renderYesNoTable()}
            {section.fields.map(renderField)}
          </>
        );
      case 'musclePalpation':
        return (
          <>
            {renderMusclePalpation()}
            {section.fields.map(renderField)}
          </>
        );
      case 'mixed':
      default:
        return section.fields.map(renderField);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!section) {
    return (
      <div className="p-6">
        <p className="text-red-600">Invalid section number.</p>
        <Link to={`/patients/${patientId}/cases/${caseId}/exam`} className="text-blue-600 text-sm">
          Back to exam overview
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2 flex items-baseline gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Exam</h1>
        <span className="text-base sm:text-lg font-semibold text-blue-600">
          {String(section.number).padStart(2, '0')} {section.name}
        </span>
      </div>

      {/* Tab Navigation */}
      <div className="relative border-b border-gray-200">
        <div className="flex items-center">
          <button
            onClick={() => sectionNum > 1 && goToSection(sectionNum - 1)}
            disabled={sectionNum <= 1}
            className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-700 disabled:opacity-30"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div ref={tabsRef} className="flex-1 overflow-x-auto flex scrollbar-hide">
            {EXAM_SECTIONS.map((s) => {
              const isActive = s.number === sectionNum;
              const hasData = examData[s.id] && Object.keys(examData[s.id]).length > 0;
              return (
                <button
                  key={s.id}
                  data-active={isActive ? 'true' : 'false'}
                  onClick={() => goToSection(s.number)}
                  className={`flex-shrink-0 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition ${
                    isActive
                      ? 'border-blue-600 text-blue-600'
                      : hasData
                        ? 'border-transparent text-gray-700 hover:text-blue-500'
                        : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {isActive ? (
                    <span>{String(s.number).padStart(2, '0')}  {s.shortName || s.name}</span>
                  ) : (
                    <span>{String(s.number).padStart(2, '0')}</span>
                  )}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => sectionNum < 17 && goToSection(sectionNum + 1)}
            disabled={sectionNum >= 17}
            className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-700 disabled:opacity-30"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Section Content */}
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        {renderSectionContent()}
      </div>

      {/* Bottom Nav */}
      <div className="sticky bottom-0 bg-white border-t border-gray-100 px-4 sm:px-6 py-3 flex items-center justify-between">
        <Link
          to={`/patients/${patientId}/cases/${caseId}/exam`}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          Overview
        </Link>
        <button
          onClick={handleNext}
          className="inline-flex items-center px-5 sm:px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold rounded-full hover:from-blue-700 hover:to-indigo-700 transition shadow-lg"
        >
          {sectionNum < 17 ? 'NEXT' : 'FINISH'}
          <svg className="w-4 h-4 ml-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ExamForm;
